/** @jsxImportSource ../../i18n */
import React, { forwardRef } from 'react';
import { cx } from '../../theme/cx';

const PADDINGS = { none: '', sm: 'p-4', md: 'p-5 sm:p-6', lg: 'p-6 sm:p-8' };

const TONES = {
  default: 'border-line bg-surface-1',
  inset: 'border-line bg-surface-2',
  gold: 'border-line bg-surface-1 bg-[radial-gradient(900px_320px_at_0%_0%,rgb(var(--c-gold-500)/0.09),transparent_62%)] shadow-[inset_0_1px_0_0_rgb(var(--c-gold-500)/0.32),0_24px_48px_-32px_rgb(0_0_0/0.7)]',
};

export const Card = forwardRef(({ as: Component = 'div', padding = 'md', tone = 'default', interactive = false, className, children, ...rest }, ref) => (
  <Component
    ref={ref}
    className={cx(
      'min-w-0 rounded-card border',
      tone !== 'gold' && 'shadow-card',
      TONES[tone] || TONES.default,
      PADDINGS[padding] ?? PADDINGS.md,
      interactive && 'transition-colors duration-200 ease-standard hover:border-line-strong hover:bg-surface-2',
      className,
    )}
    {...rest}
  >
    {children}
  </Component>
));

const TILE_TONES = {
  gold: 'text-gold-400',
  neutral: 'text-fg-muted',
  gain: 'text-gain-400',
  loss: 'text-loss-400',
  warn: 'text-warn-400',
  info: 'text-info-400',
  trade: 'text-trade-400',
};

const TILE_SIZES = { sm: ['h-8 w-8', 15], md: ['h-10 w-10', 18], lg: ['h-12 w-12', 20] };

export const IconTile = ({ icon: Icon, tone = 'gold', size = 'md', className }) => {
  const [sizeClass, iconSize] = TILE_SIZES[size] || TILE_SIZES.md;
  return (
    <span className={cx('inline-flex flex-shrink-0 items-center justify-center rounded-control border border-line bg-surface-2 shadow-[inset_0_1px_0_rgb(243_239_230/0.04)]', TILE_TONES[tone] || TILE_TONES.gold, sizeClass, className)} aria-hidden="true">
      {Icon && <Icon size={iconSize} />}
    </span>
  );
};

export const CardHeader = ({ eyebrow, title, description, icon, iconTone = 'gold', actions, titleAs: Title = 'h2', className, children }) => (
  <div className={cx('mb-5 flex flex-wrap items-start justify-between gap-x-4 gap-y-3', className)}>
    <div className="flex min-w-0 flex-1 basis-48 items-start gap-3">
      {icon && <IconTile icon={icon} tone={iconTone} size="sm" className="mt-0.5" />}
      <div className="min-w-0">
        {eyebrow && <p className="eyebrow mb-1">{eyebrow}</p>}
        {title && <Title className="text-[17px] font-semibold leading-6 tracking-[-0.005em] text-fg">{title}</Title>}
        {description && <p className="mt-1 text-sm leading-relaxed text-fg-muted">{description}</p>}
        {children}
      </div>
    </div>
    {actions && <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{actions}</div>}
  </div>
);
